/**
 * Sección "Privacidad" — Habeas Data (Ley 1581 / 2012).
 *
 * 4 capacidades:
 *   • Ver consentimientos vigentes (con fecha de aceptación + versión)
 *   • Descargar todos mis datos (JSON, genera y descarga inline)
 *   • Activar/revocar marketing en un toggle
 *   • Links a los 4 documentos legales
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Download, ExternalLink, Loader2 } from 'lucide-react'

import {
  ActionButton,
  DefList,
  DefRow,
  InlineAlert,
  SettingsSection,
} from '@/components/ui/settings-primitives'
import { accountApi } from '@/lib/api'

interface ConsentInfo {
  kind: string
  version: string
  accepted_at: string
  is_current: boolean
}

interface ConsentsResponse {
  consents: ConsentInfo[]
}

const KIND_LABELS: Record<string, string> = {
  terms: 'Términos y Condiciones',
  privacy: 'Política de Privacidad',
  'habeas-data': 'Autorización Habeas Data',
  aviso: 'Aviso de Privacidad', // legacy — solo aparece en consents históricos
  cookies: 'Política de Cookies',
  marketing: 'Comunicaciones de marketing',
}

export function PrivacySection() {
  const [consents, setConsents] = useState<ConsentInfo[] | null>(null)
  const [loadingConsents, setLoadingConsents] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [savingMarketing, setSavingMarketing] = useState(false)

  const load = async () => {
    setLoadingConsents(true)
    try {
      const r = await accountApi.get<ConsentsResponse>('/api/v1/me/consents')
      setConsents(r.data.consents)
    } catch {
      toast.error('No pudimos cargar tus consentimientos')
    } finally {
      setLoadingConsents(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const onExport = async () => {
    setExporting(true)
    try {
      const r = await accountApi.get<unknown>('/api/v1/me/data-export')
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `taxor-mis-datos-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Tus datos están listos para descarga')
    } catch {
      toast.error('No pudimos generar el export')
    } finally {
      setExporting(false)
    }
  }

  const marketingActive = !!consents?.find((c) => c.kind === 'marketing')

  const toggleMarketing = async () => {
    setSavingMarketing(true)
    try {
      if (marketingActive) {
        await accountApi.post('/api/v1/me/consents/revoke', {
          kind: 'marketing',
          reason: 'user_revoked_marketing_in_profile',
        })
        toast.success('Comunicaciones de marketing desactivadas')
      } else {
        await accountApi.post('/api/v1/me/consents/accept', { kind: 'marketing' })
        toast.success('Comunicaciones de marketing activadas')
      }
      await load()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos guardar el cambio')
    } finally {
      setSavingMarketing(false)
    }
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Tus consentimientos vigentes"
        description="Conforme a la Ley 1581 de 2012 (Habeas Data Colombia), aquí ves cada consentimiento que diste, cuándo, y a qué versión del documento."
      >
        {loadingConsents ? (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : !consents || consents.length === 0 ? (
          <InlineAlert variant="warning">
            No vemos consentimientos activos. Esto es inusual — escríbenos a
            contacto@taxor.co para revisar.
          </InlineAlert>
        ) : (
          <DefList>
            {consents.map((c) => (
              <DefRow key={c.kind} term={KIND_LABELS[c.kind] ?? c.kind}>
                <div className="text-right">
                  <span className="text-sm text-fg-primary">
                    Versión {c.version}
                    {c.is_current ? null : (
                      <span className="ml-1 text-warning-fg" title="Hay una versión más nueva">
                        (versión antigua)
                      </span>
                    )}
                  </span>
                  <br />
                  <span className="text-xs text-fg-muted">
                    Aceptada {new Date(c.accepted_at).toLocaleString('es-CO')}
                  </span>
                </div>
              </DefRow>
            ))}
          </DefList>
        )}
      </SettingsSection>

      <SettingsSection
        title="Descargar mis datos"
        description="Derecho de acceso (art. 8 lit. a Ley 1581). Descarga un JSON con todos tus datos personales: perfil, sesiones, audit log, OAuth conectados, organizaciones y consentimientos."
        footer={
          <ActionButton onClick={onExport} loading={exporting} disabled={exporting}>
            <Download className="h-4 w-4" />
            Descargar mis datos (JSON)
          </ActionButton>
        }
      >
        <p className="text-sm text-fg-secondary">
          La descarga es síncrona y se hace al instante. NO incluye hashes de contraseña
          ni secretos de 2FA por seguridad.
        </p>
      </SettingsSection>

      <SettingsSection
        title="Comunicaciones de marketing"
        description="Recibir o no comunicaciones promocionales de Taxor. Este consentimiento es opcional y puedes cambiarlo cuando quieras."
        footer={
          <ActionButton
            variant={marketingActive ? 'danger' : 'primary'}
            onClick={toggleMarketing}
            loading={savingMarketing}
          >
            {marketingActive ? 'Desactivar marketing' : 'Activar marketing'}
          </ActionButton>
        }
      >
        <p className="text-sm text-fg-secondary">
          Estado actual:{' '}
          <strong>{marketingActive ? 'Activo' : 'Desactivado'}</strong>.
        </p>
      </SettingsSection>

      <SettingsSection
        title="Documentos legales"
        description="Las versiones vigentes de los documentos que rigen tu cuenta."
      >
        <ul className="divide-y divide-border">
          {(['terminos', 'privacidad', 'habeas-data', 'cookies'] as const).map((slug) => (
            <li key={slug} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
              <span className="text-sm text-fg-primary">{labelOf(slug)}</span>
              <Link
                to={`/legal/${slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
              >
                Leer <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </li>
          ))}
        </ul>
      </SettingsSection>

      <SettingsSection
        title="Revocar autorización general"
        description="Si quieres dejar de usar Taxor y revocar la autorización general de tratamiento de datos, equivale a eliminar tu cuenta."
      >
        <InlineAlert variant="info">
          Para revocar tu autorización general (privacidad / términos), usa{' '}
          <strong>Mi cuenta → Zona de peligro → Eliminar mi cuenta</strong>. Tras 30
          días de gracia eliminamos permanentemente tus datos.
        </InlineAlert>
        <p className="mt-3 text-xs text-fg-muted">
          Reclamaciones de habeas data: <strong>contacto@taxor.co</strong>. La SIC
          (Superintendencia de Industria y Comercio) es la autoridad de control.
        </p>
      </SettingsSection>
    </div>
  )
}

function labelOf(slug: 'terminos' | 'privacidad' | 'habeas-data' | 'cookies'): string {
  switch (slug) {
    case 'terminos':
      return 'Términos y Condiciones'
    case 'privacidad':
      return 'Política de Privacidad'
    case 'habeas-data':
      return 'Autorización Habeas Data'
    case 'cookies':
      return 'Política de Cookies'
  }
}
